import { useState } from "react";
import { CalendarDays, X, ArrowRight } from "lucide-react";
import { format, parseISO, isValid, addMonths, addDays, startOfMonth, endOfMonth, subDays } from "date-fns";
import { nb } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";

const toISO = (d) => format(d, "yyyy-MM-dd");
const fmt = (d) => format(d, "dd.MM.yyyy", { locale: nb });
const parse = (v) => {
  if (!v) return undefined;
  const d = parseISO(v);
  return isValid(d) ? d : undefined;
};

/** Default quick presets (relative to today). Pass `presets={[]}` to hide. */
export const DEFAULT_PRESETS = [
  { label: "I dag", fn: (t) => t, testid: "today" },
  { label: "+1 md", fn: (t) => addMonths(t, 1), testid: "plus-1m" },
  { label: "+3 md", fn: (t) => addMonths(t, 3), testid: "plus-3m" },
  { label: "+6 md", fn: (t) => addMonths(t, 6), testid: "plus-6m" },
  { label: "+12 md", fn: (t) => addMonths(t, 12), testid: "plus-12m" },
];

const Chips = ({ presets, onPick, testid, size = "sm", value }) => {
  if (!presets || presets.length === 0) return null;
  const today = new Date();
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-presets`}>
      {presets.map((p) => {
        const iso = toISO(p.fn(today));
        const active = value === iso;
        return (
          <button
            key={p.label}
            type="button"
            onClick={() => onPick(iso)}
            data-testid={`${testid}-preset-${p.testid || p.label.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
            className={cn(
              "rounded-full border bg-card font-semibold transition-colors duration-150 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
              size === "lg" ? "h-10 px-4 text-sm" : "h-8 px-3 text-xs",
              active && "border-transparent bg-[#0B0D10] text-white hover:bg-[#0B0D10] dark:bg-white dark:text-[#0B0D10]"
            )}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
};

const Trigger = ({ id, disabled, testid, className, valid, label, placeholder, clearable, onClear, size = "md", ...rest }) => (
  <Button
    id={id}
    type="button"
    variant="outline"
    disabled={disabled}
    data-testid={testid}
    className={cn(
      "w-full justify-start bg-card px-3 font-medium shadow-sm",
      size === "lg" ? "h-12 text-base" : "h-12 md:h-11",
      !valid && "font-normal text-muted-foreground",
      className
    )}
    {...rest}
  >
    <CalendarDays className={cn("h-4 w-4 shrink-0", valid ? "text-primary" : "text-muted-foreground")} />
    <span className="flex-1 truncate text-left tabular-nums">{valid ? label : placeholder}</span>
    {clearable && valid && !disabled && (
      <span
        role="button"
        tabIndex={0}
        aria-label="Fjern dato"
        data-testid={`${testid}-clear`}
        onClick={(e) => { e.stopPropagation(); onClear(); }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onClear(); } }}
        className="-mr-1 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </span>
    )}
  </Button>
);

/**
 * DatePicker v2 – value/onChange are ISO strings (YYYY-MM-DD), displayed as dd.MM.yyyy.
 * Desktop (>= md): Popover with preset chips + dropdown month/year calendar.
 * Mobile: bottom Drawer with large chips, 44px day cells, "Nullstill" / "Ferdig".
 */
export const DatePicker = ({
  value,
  onChange,
  placeholder = "Vel dato",
  clearable = true,
  disabled = false,
  className,
  testid = "date-picker",
  id,
  presets = DEFAULT_PRESETS,
  title = "Vel dato",
  description,
  size = "md",
  fromYear,
  toYear,
}) => {
  const [open, setOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const date = parse(value);
  const valid = !!date;

  const pick = (iso, close = true) => {
    onChange(iso);
    if (close) setOpen(false);
  };

  const trigger = (
    <Trigger id={id} disabled={disabled} testid={testid} className={className} valid={valid} label={valid ? fmt(date) : ""}
      placeholder={placeholder} clearable={clearable} onClear={() => onChange("")} size={size} />
  );

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent className="w-[340px] p-0" align="start" data-testid={`${testid}-popover`}>
          {presets?.length > 0 && <div className="border-b px-3 py-2.5"><Chips presets={presets} onPick={(iso) => pick(iso)} testid={testid} value={value} /></div>}
          <Calendar
            mode="single"
            captionLayout="dropdown-buttons"
            fromYear={fromYear}
            toYear={toYear}
            selected={date}
            defaultMonth={date}
            onSelect={(d) => pick(d ? toISO(d) : "")}
            initialFocus
          />
          <div className="flex items-center justify-between border-t px-3 py-2">
            <button type="button" data-testid={`${testid}-today`} className="text-xs font-semibold text-primary hover:underline" onClick={() => pick(toISO(new Date()))}>I dag</button>
            {clearable && (
              <button type="button" data-testid={`${testid}-reset-button`} disabled={!valid} className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-40" onClick={() => pick("")}>Nullstill</button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <div onClick={() => !disabled && setOpen(true)} className="contents">{trigger}</div>
      <DrawerContent data-testid={`${testid}-drawer`}>
        <DrawerHeader>
          <DrawerTitle>{title}</DrawerTitle>
          <DrawerDescription>{description || (valid ? `Vald: ${fmt(date)}` : "Trykk på ein dag, eller bruk hurtigval.")}</DrawerDescription>
        </DrawerHeader>
        <DrawerBody className="space-y-3 pb-2">
          <Chips presets={presets} onPick={(iso) => pick(iso)} testid={testid} size="lg" value={value} />
          <div className="rounded-[var(--radius)] border bg-card">
            <Calendar
              mode="single"
              size="lg"
              captionLayout="dropdown-buttons"
              fromYear={fromYear}
              toYear={toYear}
              selected={date}
              defaultMonth={date}
              onSelect={(d) => pick(d ? toISO(d) : "", true)}
              className="mx-auto w-fit"
            />
          </div>
        </DrawerBody>
        <DrawerFooter className="grid grid-cols-2 gap-3">
          <Button variant="outline" className="h-12" disabled={!valid || !clearable} onClick={() => pick("")} data-testid={`${testid}-reset-button`}>Nullstill</Button>
          <Button className="h-12" onClick={() => setOpen(false)} data-testid={`${testid}-done-button`}>Ferdig</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

const RANGE_PRESETS = [
  { label: "Denne månaden", fn: (t) => [startOfMonth(t), endOfMonth(t)], testid: "this-month" },
  { label: "Siste 30 dagar", fn: (t) => [subDays(t, 30), t], testid: "last-30" },
  { label: "Siste 90 dagar", fn: (t) => [subDays(t, 90), t], testid: "last-90" },
  { label: "I år", fn: (t) => [new Date(t.getFullYear(), 0, 1), new Date(t.getFullYear(), 11, 31)], testid: "this-year" },
  { label: "Neste 30 dagar", fn: (t) => [t, addDays(t, 30)], testid: "next-30" },
];

/**
 * DateRangePicker – from/to ISO strings. One trigger ("dd.MM.yyyy – dd.MM.yyyy"), range calendar,
 * quick presets. Desktop: Popover (2 months). Mobile: Drawer (1 month, 44px cells).
 */
export const DateRangePicker = ({ from, to, onChange, placeholder = "Periode", className, testid = "date-range", disabled = false }) => {
  const [open, setOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const f = parse(from);
  const t = parse(to);
  const valid = !!(f || t);
  const label = f && t ? `${fmt(f)} – ${fmt(t)}` : f ? `Frå ${fmt(f)}` : t ? `Til ${fmt(t)}` : "";

  const onSelect = (range) => {
    onChange({ from: range?.from ? toISO(range.from) : "", to: range?.to ? toISO(range.to) : "" });
    if (range?.from && range?.to && isDesktop) setOpen(false);
  };
  const applyPreset = (p) => {
    const [a, b] = p.fn(new Date());
    onChange({ from: toISO(a), to: toISO(b) });
    setOpen(false);
  };
  const clear = () => onChange({ from: "", to: "" });

  const trigger = (
    <Trigger disabled={disabled} testid={testid} className={className} valid={valid} label={label} placeholder={placeholder} clearable onClear={clear} />
  );
  const chips = (lg) => (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-presets`}>
      {RANGE_PRESETS.map((p) => (
        <button key={p.label} type="button" onClick={() => applyPreset(p)} data-testid={`${testid}-preset-${p.testid}`}
          className={cn("rounded-full border bg-card font-semibold transition-colors duration-150 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60", lg ? "h-10 px-4 text-sm" : "h-8 px-3 text-xs")}>
          {p.label}
        </button>
      ))}
    </div>
  );
  const selected = { from: f, to: t };

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end" data-testid={`${testid}-popover`}>
          <div className="border-b px-3 py-2.5">{chips(false)}</div>
          <Calendar mode="range" numberOfMonths={2} selected={selected} defaultMonth={f} onSelect={onSelect} initialFocus />
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs">
            <span className="text-muted-foreground">{f && !t ? "Vel sluttdato" : "Vel start- og sluttdato"}</span>
            <button type="button" className="font-semibold text-muted-foreground hover:text-foreground disabled:opacity-40" disabled={!valid} onClick={() => { clear(); setOpen(false); }} data-testid={`${testid}-reset-button`}>Nullstill</button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <div onClick={() => !disabled && setOpen(true)} className="contents">{trigger}</div>
      <DrawerContent data-testid={`${testid}-drawer`}>
        <DrawerHeader>
          <DrawerTitle>Vel periode</DrawerTitle>
          <DrawerDescription className="flex items-center gap-1.5 tabular-nums">
            {f ? fmt(f) : "Start"} <ArrowRight className="h-3.5 w-3.5" /> {t ? fmt(t) : "Slutt"}
          </DrawerDescription>
        </DrawerHeader>
        <DrawerBody className="space-y-3 pb-2">
          {chips(true)}
          <div className="rounded-[var(--radius)] border bg-card">
            <Calendar mode="range" size="lg" selected={selected} defaultMonth={f} onSelect={onSelect} className="mx-auto w-fit" captionLayout="dropdown-buttons" />
          </div>
        </DrawerBody>
        <DrawerFooter className="grid grid-cols-2 gap-3">
          <Button variant="outline" className="h-12" disabled={!valid} onClick={clear} data-testid={`${testid}-reset-button`}>Nullstill</Button>
          <Button className="h-12" onClick={() => setOpen(false)} data-testid={`${testid}-done-button`}>Ferdig</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
